import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { patientService } from '@/services/patientService';
import PatientSelfServiceForm from '@/components/patient/PatientSelfServiceForm';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2, AlertCircle, Clock } from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import { ConsentGate } from '@/components/consent';

const PatientFormPublic = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState<any>(null);
  const [clinicName, setClinicName] = useState('');
  const [clinicId, setClinicId] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [patientData, setPatientData] = useState<any>(null);
  const [submittedPatientId, setSubmittedPatientId] = useState<string | null>(null);
  const [submittedPatientName, setSubmittedPatientName] = useState<string>('');
  const [consentsDone, setConsentsDone] = useState(false);
  const [consentPhase, setConsentPhase] = useState<'booking' | 'telehealth'>('booking');

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('Invalid form link. Please contact your healthcare provider.');
        setLoading(false);
        return;
      }

      try {
        const response = await patientService.validatePatientFormToken(token);
        
        if (response.success && response.data) {
          setClinicName(response.data.clinic?.name || 'Your Healthcare Provider');
          setClinicId(response.data.clinic?.id || null);
          setExpiresAt(response.data.expires_at);
          setPatientData(response.data.patient);
          setFormData(response.data);
        } else {
          setError(response.message || 'Invalid or expired form link.');
        }
      } catch (err: any) {
        console.error('Error validating token:', err);
        setError(
          err?.response?.data?.error?.message ||
          err?.message ||
          'Unable to load form. This link may have expired or is invalid.'
        );
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (data: any) => {
    if (!token) return;

    setSubmitting(true);
    try {
      const response = await patientService.submitPatientForm(token, data);

      if (response.success) {
        const newPatientId = response.data?.patient_id ?? patientData?.id ?? null;
        setSubmittedPatientId(newPatientId);
        const fullName = [data?.first_name, data?.last_name].filter(Boolean).join(' ').trim();
        setSubmittedPatientName(fullName);
        setConsentPhase('booking');
        setConsentsDone(false);
        setSuccess(true);
        toast({
          title: 'Success',
          description: patientData
            ? 'Your information has been updated successfully.'
            : 'Your information has been submitted successfully.',
          variant: 'success',
        });
      } else {
        toast({
          title: 'Error',
          description: response.message || 'Failed to submit form. Please try again.',
          variant: 'destructive',
        });
      }
    } catch (err: any) {
      console.error('Error submitting form:', err);
      toast({
        title: 'Error',
        description: err?.response?.data?.error?.message || err?.message || 'Failed to submit form. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate if expiring soon (within 3 days)
  const isExpiringSoon = expiresAt && differenceInDays(new Date(expiresAt), new Date()) <= 3;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-brand-teal border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="w-full max-w-md">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="ml-2">
              {error}
            </AlertDescription>
          </Alert>
          <p className="text-center text-slate-600 mt-4">
            Please contact your healthcare provider for assistance.
          </p>
        </div>
      </div>
    );
  }

  if (success && !consentsDone && clinicId && submittedPatientId) {
    const advancePhase = () => {
      if (consentPhase === 'booking') {
        setConsentPhase('telehealth');
      } else {
        setConsentsDone(true);
      }
    };

    return (
      <div className="min-h-screen bg-slate-50 py-8 overflow-auto h-screen">
        <div className="w-full max-w-3xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-brand-teal rounded-full flex items-center justify-center">
                <span className="text-white text-xl font-bold">
                  {clinicName.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <h1 className="text-xl font-semibold text-slate-900">{clinicName}</h1>
                <p className="text-sm text-slate-500">Almost done — please review the consent forms below.</p>
              </div>
            </div>
          </div>

          <ConsentGate
            key={consentPhase}
            clinicId={clinicId}
            patientId={submittedPatientId}
            patientName={submittedPatientName}
            purpose={consentPhase}
            onComplete={advancePhase}
            onCancel={advancePhase}
            allowAutoCompleteOnMount
          />

          <p className="mt-4 text-center text-xs text-slate-500">
            You can also sign these later — we'll prompt you again before your first appointment.
          </p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              {patientData ? 'Information Updated!' : 'Thank You!'}
            </h2>
            <p className="text-slate-600 mb-6">
              {patientData
                ? 'Your patient information has been updated successfully.'
                : 'Your patient information has been submitted successfully. The clinic will review your information and contact you if needed.'
              }
            </p>
            <p className="text-sm text-slate-500">
              You can close this window now.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 overflow-auto h-screen">
      <div className="w-full max-w-4xl mx-auto px-4">
        {/* Expiration Warning */}
        {isExpiringSoon && expiresAt && (
          <Alert className="mb-6 border-orange-200 bg-orange-50">
            <Clock className="h-4 w-4 text-orange-600" />
            <AlertDescription className="ml-2 text-orange-800">
              This form link will expire on {format(new Date(expiresAt), "MMMM d, yyyy 'at' h:mm a")}. 
              Please complete it soon.
            </AlertDescription>
          </Alert>
        )}

        {/* Clinic Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-brand-teal rounded-full flex items-center justify-center">
              <span className="text-white text-xl font-bold">
                {clinicName.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h1 className="text-xl font-semibold text-slate-900">{clinicName}</h1>
              <p className="text-sm text-slate-500">Patient Information Form</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow-sm">
          <PatientSelfServiceForm
            clinicName={clinicName}
            patientData={patientData}
            onSubmit={handleSubmit}
            isLoading={submitting}
          />
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-slate-500">
          <p>
            This is a secure form. Your information is encrypted and will only be shared with {clinicName}.
          </p>
          {expiresAt && (
            <p className="mt-2">
              This form link expires on {format(new Date(expiresAt), 'MMMM d, yyyy')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientFormPublic;

